import { CartSize } from './cart-size';
import { CollectionFrequency } from './collection-frequency';
import { CollectionHow } from './collection-how';
import { CollectionWho } from './collection-who';
import { MultiFamilyCollectionType } from './collection-type';
import { MultiFamilyContainer, SingleFamilyContainer } from './container';
import { ContaminationControl } from './contamination-control';
import { ContaminationRateMethod } from './contamination-rate-method';
import { SetOutRateMethod } from './setout-rate-method';
import { NDBAPIMRF } from './mrf';
import { ParticipationRateMethod } from './participation-rate-method';
import { RecyclingCategory } from './recycling-category';
import { SimilarCommunity } from './similar-community';
import { YearType } from './year-type';
import { RateOrFeeType } from './solid-waste';

export function hasProgram(communityInformation: PublicCommunityInformation) {
  return (
    communityInformation.hasProgramDropoff ||
    communityInformation.hasProgramMultiFamily ||
    communityInformation.hasProgramSingleFamily
  );
}

export type PrivateCommunityInformation = CommunityInformation & {
  visibility: 'private';
  numberOfFullTimeStaff?: number | undefined;
  outreachBudgetPerHouseHold?: number | undefined;
};

export type PublicCommunityInformation = {
  visibility: 'public';
} & Omit<
  CommunityInformation,
  // PSO: investigate this for now I have just added 'contaminationRateHow'
  // TODO: separate public and private properties (inside the programs)
  // | 'singleFamilyPrograms'
  // | 'multiFamilyPrograms'
  // | 'dropoffPrograms'

  | 'householdsServedSingleFamily'
  | 'householdsServedMultiFamily'
  | 'hasMultiFamilyEducation'
  | 'hasMultiFamilyTechnicalSupport'
  | 'hasMultiFamilyEnforcement'
  | 'poundsPerHousehold'
  | 'totalTonnage'
  | 'singleFamilyTonnage'
  | 'multiFamilyTonnage'
  | 'dropoffTonnage'
  | 'contaminationRatePercentage'
  | 'contaminationRateHow'
  | 'setOutRatePercentage'
  | 'metricTonsCO2AvoidedPerYear'
  | 'contaminationControls'
  | 'outreachBudgetPerHouseHold'
>;

export type AccessType =
  | 'Curbside'
  | 'Subscription'
  | 'Dropoff'
  | 'None'
  | null;
export const ACCESS_TYPES: AccessType[] = [
  'Curbside',
  'Dropoff',
  'Subscription',
  'None',
];
export type ProgramType = 'Single-Family' | 'Single-Family-Solid-Waste' | 'Multifamily' | 'Drop-Off';

export interface MetaCommunityInformation {
  communityId: string;
  updatedAtUtc: string;
}
export interface CommunityInformation extends UpdatableCommunityInformation {
  communityId: string;
  name: string;
  fullDescriptiveName: string;
  communityType: string;
  counties: Array<{
    id: string;
    name: string;
  }>;
  county: string;
  gallonsWaterSavedPerYear: number;
  incomeMean: number;
  metricTonsCO2AvoidedPerYear: number;
  occupiedHousingUnits: number;
  percentBachelorsOrHigher?: number;
  percentRaceBipoc?: number;
  programType: ProgramType[] | null | undefined;
  similarCommunities?: SimilarCommunity[];
  state: {
    id: string;
    name: string;
    two_letter_code: string;
  };
  tonsSavedPerYear: number;
  totalHousingUnits: number;
  totalPopulation: number;
  accessType: AccessType;
  suffix: string;
  updatedAtUtc: string;
}
export interface ProgramSharedProperties {
  id: number;
  // REFACTOR: move this to the submission parent
  programId: number;
  // represent temporary frontend state that can be restored
  softProgramTerminated?: boolean;
  softProgramAdded?: boolean;
  programTerminated?: boolean;
}

export interface SingleFamilyProgram extends ProgramSharedProperties {
  materialsAccepted: Array<RecyclingCategory['id']>;
  collectionHow?: CollectionHow;
  collectionWho?: CollectionWho;
  collectionFrequency?: CollectionFrequency;
  containerTypes?: SingleFamilyContainer[];
  cartSizes: CartSize[];
  contaminationControls?: ContaminationControl[];
  programUrl?: string;
  pdfUrl?: string;
  guidelinesUrl?: string;
}

export interface SingleFamilySolidWasteProgram extends ProgramSharedProperties {
  materialsAccepted: Array<RecyclingCategory['id']>;
  householdsServed?: number | null;
  // Enumerated values stored as IDs (DATA-1667). Each `…Id`/`…Ids` field has an
  // `…Other` free-text companion, populated when the selected option `isOther`.
  collectionTypeId?: number | null;
  collectionTypeOther?: string | null;
  collectionWhoId?: number | null;
  collectionWhoOther?: string | null;
  containerTypeId?: number | null;
  containerTypeOther?: string | null;
  containerProviderId?: number | null;
  containerProviderOther?: string | null;
  frequencyOfCollectionId?: number | null;
  frequencyOfCollectionOther?: string | null;
  fundingSourceIds?: number[] | null;
  fundingSourceOther?: string | null;
  wasteGenerationDisincentiveIds?: number[] | null;
  wasteGenerationDisincentiveOther?: string | null;
  hasOrdinance?: boolean | null;
  ordinanceUrl?: string | null;
  mswTipFee?: number | null;
  rateOrFee?: number | null;
  rateOrFeeType?: RateOrFeeType | null;
  periodRateOrFee?: number | null;
  haulers?: string | null;
  websiteUrl?: string | null;
  tonnage?: number;
}
export interface MultiFamilyProgram extends ProgramSharedProperties {
  materialsAccepted: Array<RecyclingCategory['id']>;
  collectionType?: MultiFamilyCollectionType;
  additionalServices?: string[];
  hasOrdinance?: boolean;
  hasEducation?: boolean;
  hasTechnicalSupport?: boolean;
  hasEnforcement?: boolean;
  containerTypes?: MultiFamilyContainer[];
  collectionFrequency?: CollectionFrequency;
  numberOfProperties?: number;
  numberOfPropertiesWithAccess?: number;
  ordinanceUrl?: string;
  programUrl?: string;
  pdfUrl?: string;
  guidelinesUrl?: string;
}

export interface DropoffProgram extends ProgramSharedProperties {
  materialsAccepted: Array<RecyclingCategory['id']>;
  programUrl?: string;
  pdfUrl?: string;
  guidelinesUrl?: string;
}

export type AnyRecyclingProgram =
  | SingleFamilyProgram
  | MultiFamilyProgram
  | DropoffProgram;

export interface SingleFamilyProgramWithType extends SingleFamilyProgram {
  type: 'singleFamilyPrograms';
}

export interface SingleFamilySolidWasteProgramWithType extends SingleFamilySolidWasteProgram {
  type: 'singleFamilySolidWastePrograms';
}

export interface MultiFamilyProgramWithType extends MultiFamilyProgram {
  type: 'multiFamilyPrograms';
}

export interface DropoffProgramWithType extends DropoffProgram {
  type: 'dropoffPrograms';
}

export type CommunityProgramWithType =
  | SingleFamilyProgramWithType
  | SingleFamilySolidWasteProgramWithType
  | MultiFamilyProgramWithType
  | DropoffProgramWithType;

export interface UpdatableCommunityInformation {
  hasProgramDropoff: boolean;
  hasProgramMultiFamily: boolean;
  hasProgramSingleFamily: boolean;
  hasMultiFamilyOrdinance: boolean;
  hasProgramSingleFamilySolidWaste: boolean;
  contaminationControls?: ContaminationControl[];
  contaminationRatePercentage?: number;
  guidelinesWebsite?: string;
  pdfUrl?: string;

  multiFamilyCollectionFrequency?: CollectionFrequency;
  onlineSearchUrl?: string;
  outreachBudgetPerHouseHold?: number;
  mrfs?: Array<NDBAPIMRF>;

  setOutRatePercentage?: number;

  numberOfFullTimeStaff?: number;
  totalTonnage?: number;
  occupancyRate?: number;

  poundsPerHousehold?: number;

  singleFamilyPrograms?: SingleFamilyProgram[];
  singleFamilySolidWastePrograms?: SingleFamilySolidWasteProgram[];
  multiFamilyPrograms?: MultiFamilyProgram[];
  dropoffPrograms?: DropoffProgram[];
}

export interface ZipCommunity {
  communityId: string;
  communityName: string;
  communityNamePublic: string;
  zipCode: string;
  state: string;
}
